import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Info, Loader2 } from "@fg/icons";
import { useAccessToken } from "@/auth/useAccessToken";
import { driveApi, explainSaveError, isDriveUrl, type DriveItem } from "@/api/driveApi";
import { useSaveTracker } from "@/lib/useSaveTracker";
import { CloseFileButton, FileTitle } from "@/features/shell/FileTitle";
import { unsavedChangesDialog } from "@fg/ui-theme/dialogs";
import { ShellHeaderPortal, useShellLeaveGuard } from "@/features/shell/ShellHeader";
import type { EditorLaunchState } from "@/features/home/homeModel";
import { DriveOpenDialog } from "./DriveOpenDialog";
import { buildSheetTemplate } from "./sheetTemplates";
import { driveFileApi } from "@/features/drive-file/driveFileApi";
import { DriveLocation } from "@/features/drive-file/DriveLocation";
import { ShareButton } from "@/features/drive-file/ShareDialog";
import {
    HistoryButton, VersionHistoryPanel, VIEWER_HISTORY_MESSAGE, isHistoryShortcut
} from "@/features/drive-file/VersionHistoryPanel";
import {
    CommentsButton, CommentsPanel, isCommentsShortcut, useOpenCommentCount
} from "@/features/drive-file/CommentsPanel";
import { sheetCommentsAdapter } from "./sheetComments";
import { FreezeMenu, SheetStatusBar, type SelectionAggregate } from "./SheetTools";
import type { FileMenuItem, SaveAsFormat, WorkbookEditorHandle } from "@/features/workbook/WorkbookEditor";
import { isWorkbookJson } from "@/features/workbook/workbookJson";

// The editor is its own chunk; the spreadsheet engine itself runs in a Web Worker
// (@fg/web-core) and is fetched when the editor starts.
const WorkbookEditor = lazy(() => import("@/features/workbook/WorkbookEditor").then((m) => ({ default: m.WorkbookEditor })));

// .xlsx and .csv open natively; .json is a snapshot from the previous editor.
const OPENABLE_EXT_RE = /\.(xlsx|xlsm|csv|json)$/i;
const KNOWN_EXT_RE = /\.(xlsx|xlsm|xls|csv|json|pdf)$/i;
// Anything that reports a change right after a load belongs to the load.
const LOAD_SETTLE_MS = 250;

type SheetFormat = "xlsx" | "xlsm" | "csv";
/** What Save As writes: the formats Drive versions use, or any other type the engine lists (`fmt:<id>`). */
type SaveType = SheetFormat | `fmt:${string}`;
type SaveMode = "manual" | "auto";

/** The Drive file the editor is bound to — Save writes a new version of it. */
interface TrackedFile {
    id: string;
    name: string;
    parentId: string | null;
    /** "viewer" / "editor" when someone else shared the file; unset for your own. */
    role?: string | null;
}

export function SheetPage() {
    const [params] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const token = useAccessToken();
    // Autosave fires from a timer — read the freshest token / navigate.
    const tokenRef = useRef(token);
    tokenRef.current = token;
    const navigateRef = useRef(navigate);
    navigateRef.current = navigate;

    // Launch parameters are read once: the URL is rewritten as files are
    // opened and saved, and that must never re-trigger a load.
    const launchRef = useRef({
        fileId: params.get("fileId"),
        openFile: params.get("openFile"),
        fileName: params.get("fileName"),
        // From the home screen: a picked local file / a template id.
        localFile: (location.state as EditorLaunchState | null)?.localFile ?? null,
        template: (location.state as EditorLaunchState | null)?.template ?? null
    });

    const editorRef = useRef<WorkbookEditorHandle>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const trackedRef = useRef<TrackedFile | null>(null);
    const [tracked, setTrackedState] = useState<TrackedFile | null>(null);
    const [fileName, setFileName] = useState(launchRef.current.fileName ?? "Untitled.xlsx");
    const [loading, setLoading] = useState(false);
    const loadingRef = useRef(false);
    const settleTimerRef = useRef<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    const saveNowRef = useRef<(mode: SaveMode) => Promise<void>>(async () => {});
    const { status, markDirty, markClean, setSuppressed, runSave, hasUnsavedChanges, whenSaved } = useSaveTracker({
        onAutosave: () => void saveNowRef.current("auto"),
        onError: setError
    });

    // Binds the editor to a Drive file (null = unbound) and mirrors the
    // binding into the URL — `?fileId=` only — so a reload reopens it.
    const setTracked = useCallback((file: TrackedFile | null) => {
        const previousId = trackedRef.current?.id ?? null;
        trackedRef.current = file;
        setTrackedState(file);
        if (file) setFileName(file.name);
        if ((file?.id ?? null) !== previousId) {
            navigateRef.current(
                { search: file ? `?fileId=${encodeURIComponent(file.id)}` : "" },
                { replace: true }
            );
        }
    }, []);

    // The one prompt for unsaved changes: Save (the save runs first; if it does not finish, stay), Don't save, or Cancel.
    const resolveUnsaved = useCallback(async (): Promise<boolean> => {
        await whenSaved();
        if (!hasUnsavedChanges()) return true;
        const choice = await unsavedChangesDialog(fileName);
        if (choice === "cancel") return false;
        if (choice === "save") {
            await saveNowRef.current("manual");
            await whenSaved();
            if (hasUnsavedChanges()) return false;
        }
        return true;
    }, [fileName, hasUnsavedChanges, whenSaved]);
    useShellLeaveGuard(resolveUnsaved);

    // Close (top bar X / File → Close) → Sheets home. A save in flight
    // finishes first — never cut off; whatever is still unsaved after it asks
    // first. Replace: Back from home doesn't reopen the file just closed.
    const [closing, setClosing] = useState(false);
    const closingRef = useRef(false);
    const closeFile = useCallback(async () => {
        if (closingRef.current) return;
        closingRef.current = true;
        setClosing(true);
        try {
            await whenSaved();
            if (!(await resolveUnsaved())) return;
            markClean();
            navigateRef.current("/", { replace: true });
        } finally {
            closingRef.current = false;
            setClosing(false);
        }
    }, [markClean, resolveUnsaved]);

    // Loads file bytes into the engine: .xlsx and .csv natively, .json snapshots
    // through the converter — the same path for Drive files, `?fileId=` and local picks.
    const loadBlob = useCallback(async (ed: WorkbookEditorHandle, blob: Blob, name: string) => {
        if (/\.json$/i.test(name)) {
            let json: unknown;
            try {
                json = JSON.parse(await blob.text());
            } catch {
                throw new Error(`${name} isn't a valid workbook snapshot.`);
            }
            if (!isWorkbookJson(json)) throw new Error(`${name} isn't a workbook snapshot Workbook can read.`);
            await ed.openJson(json);
        } else if (/\.csv$/i.test(name)) {
            await ed.openCsv(await blob.text());
        } else {
            await ed.openXlsx(await blob.arrayBuffer());
        }
    }, []);

    // Wraps every workbook load. Change reports the load itself causes are
    // ignored; a failed load leaves the previous workbook's state alone.
    const runLoad = useCallback(async (load: (ed: WorkbookEditorHandle) => Promise<void>): Promise<boolean> => {
        const ed = editorRef.current;
        if (!ed) return false;
        if (settleTimerRef.current !== null) window.clearTimeout(settleTimerRef.current);
        loadingRef.current = true;
        setLoading(true);
        setError(null);
        setSuppressed(true);
        try {
            await load(ed);
            markClean();
            return true;
        } catch (err) {
            setError(errorMessage(err));
            return false;
        } finally {
            loadingRef.current = false;
            setLoading(false);
            settleTimerRef.current = window.setTimeout(() => {
                settleTimerRef.current = null;
                setSuppressed(false);
            }, LOAD_SETTLE_MS);
        }
    }, [markClean, setSuppressed]);

    const openDriveFile = useCallback((file: TrackedFile) => runLoad(async (ed) => {
        if (!OPENABLE_EXT_RE.test(file.name)) throw new Error(unsupportedMessage(file.name));
        const blob = await driveApi.downloadBlob(file.id, tokenRef.current);
        await loadBlob(ed, blob, file.name);
        setTracked(file);
    }), [loadBlob, runLoad, setTracked]);

    const onLocalPick: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
        const file = e.target.files?.[0];
        if (fileInputRef.current) fileInputRef.current.value = "";
        if (!file || !(await resolveUnsaved())) return;
        void runLoad(async (ed) => {
            if (!OPENABLE_EXT_RE.test(file.name)) throw new Error(unsupportedMessage(file.name));
            await loadBlob(ed, file, file.name);
            setTracked(null);
            setFileName(file.name);
        });
    };

    // The editor reports ready once its engine is up; then the launch request runs.
    const loadInitial = useCallback(() => {
        if (!editorRef.current) return;
        const { fileId, openFile, fileName: launchName, localFile, template } = launchRef.current;
        if (template) {
            // A home-screen template: a new, unsaved workbook. Drop the router
            // state so a reload opens blank rather than re-applying it.
            navigateRef.current({ search: "" }, { replace: true });
            const built = buildSheetTemplate(template);
            void runLoad(async (ed) => {
                if (built) await ed.openJson(built.workbook);
                setTracked(null);
                setFileName(built?.fileName ?? "Untitled.xlsx");
            });
            return;
        }
        if (localFile) {
            // Picked on the home screen (the picker needs the click's user
            // activation, which is gone by the time this chunk has loaded).
            navigateRef.current({ search: "" }, { replace: true });
            void runLoad(async (ed) => {
                if (!OPENABLE_EXT_RE.test(localFile.name)) throw new Error(unsupportedMessage(localFile.name));
                await loadBlob(ed, localFile, localFile.name);
                setTracked(null);
                setFileName(localFile.name);
            });
            return;
        }
        if (fileId) {
            // Keep `fileId` (a reload reopens the file); drop Drive's
            // one-shot `openFile` / `fileName` hints.
            navigateRef.current({ search: `?fileId=${encodeURIComponent(fileId)}` }, { replace: true });
            void runLoad(async (ed) => {
                const meta = await driveApi.getItem(fileId, tokenRef.current);
                setFileName(meta.name);
                if (!OPENABLE_EXT_RE.test(meta.name)) throw new Error(unsupportedMessage(meta.name));
                const blob = await driveApi.downloadBlob(meta.id, tokenRef.current);
                await loadBlob(ed, blob, meta.name);
                setTracked(toTracked(meta));
            });
        } else if (openFile) {
            navigateRef.current({ search: "" }, { replace: true });
            const name = launchName ?? "workbook.json";
            void runLoad(async (ed) => {
                if (!OPENABLE_EXT_RE.test(name)) throw new Error(unsupportedMessage(name));
                // Only Drive gets the bearer token — `openFile` comes from the URL.
                const headers = new Headers();
                if (tokenRef.current && isDriveUrl(openFile)) headers.set("Authorization", `Bearer ${tokenRef.current}`);
                const res = await fetch(openFile, { headers, credentials: "omit" });
                if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
                await loadBlob(ed, await res.blob(), name);
                setFileName(name);
            });
        }
    }, [loadBlob, runLoad, setTracked]);

    // Save As → Drive, or a Save that can't version the open file: creates
    // a new file in the open file's folder, then binds the editor to it so
    // the next Save versions the copy rather than the original.
    const saveAsNewDriveFile = useCallback(async (baseName: string, wanted: SaveType) => {
        const ed = editorRef.current;
        if (!ed) return;
        await runSave(async () => {
            const { blob, ext } = await renderAs(ed, wanted);
            const saved = await driveApi.uploadBlob(
                blob, `${baseName}.${ext}`, trackedRef.current?.parentId ?? null, tokenRef.current
            );
            setTracked(toTracked(saved));
        });
    }, [runSave, setTracked]);

    const saveNow = useCallback(async (mode: SaveMode) => {
        const ed = editorRef.current;
        if (!ed || loadingRef.current) return;
        const current = trackedRef.current;
        if (!current) {
            // Never saved — only an explicit Save asks where to put it.
            if (mode === "manual") setSaveAsOpen(true);
            return;
        }
        const format = versionFormat(current.name);
        if (!format) {
            // A .json snapshot or legacy .xls can't take .xlsx bytes as a new version, so an
            // explicit Save writes an .xlsx copy next to it instead.
            if (mode === "manual") await saveAsNewDriveFile(stripExtension(current.name), "xlsx");
            return;
        }
        // A view-only share can't take a new version — don't autosave into a
        // guaranteed failure; an explicit Save explains why.
        if (mode === "auto" && current.role === "viewer") return;
        if (mode === "manual") setError(null);
        // A macro workbook is an .xlsm and a plain one an .xlsx: if the open file's extension no longer
        // says so, an explicit Save writes a correctly named copy instead of a file Excel would refuse.
        if ((await resolveFormat(ed, format)) !== format) {
            if (mode === "manual") await saveAsNewDriveFile(stripExtension(current.name), format);
            return;
        }
        await runSave(async () => {
            const blob = await renderWorkbook(ed, format);
            const saved = await driveApi
                .uploadVersion(current.id, blob, current.name, tokenRef.current)
                .catch((err: unknown) => { throw explainSaveError(err, current.role); });
            if (trackedRef.current?.id === saved.id) setTracked(toTracked(saved));
        });
    }, [runSave, saveAsNewDriveFile, setTracked]);
    saveNowRef.current = saveNow;

    // Version history — owners and editors of a Drive file only.
    const [historyOpen, setHistoryOpen] = useState(false);
    const [commentsOpen, setCommentsOpen] = useState(false);
    const openHistory = useCallback(() => {
        const current = trackedRef.current;
        if (!current) setError("Save this workbook to Drive to keep a version history.");
        else if (current.role === "viewer") setError(VIEWER_HISTORY_MESSAGE);
        else {
            setCommentsOpen(false);
            setHistoryOpen(true);
        }
    }, []);
    useEffect(() => {
        if (!tracked || tracked.role === "viewer") setHistoryOpen(false);
        if (!tracked) setCommentsOpen(false);
    }, [tracked]);

    // Comments — anyone who can open the Drive file, viewers included.
    const commentCount = useOpenCommentCount(tracked?.id ?? null);
    const commentsAdapter = useMemo(() => sheetCommentsAdapter(() => editorRef.current), []);
    const openComments = useCallback(() => {
        if (!trackedRef.current) {
            setError("Save this workbook to Drive to comment on it.");
            return;
        }
        setHistoryOpen(false);
        setCommentsOpen(true);
    }, []);

    // Drive adds a new version holding the old bytes; reload it into the
    // editor and stay bound to the file.
    const restoreVersion = useCallback(async (versionId: string, discard: boolean) => {
        const current = trackedRef.current;
        if (!current) throw new Error("This workbook isn't in Drive.");
        if (!discard && hasUnsavedChanges()) throw new Error("Save or discard your changes before restoring.");
        // Dropping the edits first stops autosave writing them over the restore.
        if (discard) markClean();
        let restored: DriveItem;
        try {
            restored = await driveFileApi.restoreVersion(current.id, versionId, tokenRef.current);
        } catch (err) {
            if (discard) markDirty();
            throw err;
        }
        const ok = await openDriveFile({ ...toTracked(restored), role: restored.role ?? current.role ?? null });
        if (!ok) throw new Error("The version was restored in Drive, but the editor couldn't reload it — reopen the file to see it.");
    }, [hasUnsavedChanges, markClean, markDirty, openDriveFile]);

    // Ctrl+S → Save; Ctrl+Shift+S → Save As; Ctrl+Alt+Shift+H → Version history
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (isHistoryShortcut(e)) {
                e.preventDefault();
                openHistory();
                return;
            }
            if (isCommentsShortcut(e)) {
                e.preventDefault();
                openComments();
                return;
            }
            if (e.key.toLowerCase() === "s" && (e.ctrlKey || e.metaKey) && !e.altKey) {
                e.preventDefault();
                if (e.shiftKey) setSaveAsOpen(true);
                else void saveNowRef.current("manual");
            }
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [openHistory, openComments]);

    // Inline title rename. Bound to Drive → rename the file there; otherwise
    // it's just the name the next Save As suggests.
    const renameFile = useCallback(async (typed: string) => {
        const current = trackedRef.current;
        const next = keepExtension(typed, current?.name ?? fileName);
        if (!current) {
            setFileName(next);
            return;
        }
        const updated = await driveApi.renameItem(current.id, next, tokenRef.current);
        setTracked(toTracked(updated));
    }, [fileName, setTracked]);

    // Drive file picker modal state — triggered by File → Open from Drive.
    // (The home screen's "Open file → From Drive" uses the same dialog.)
    const [driveDialogOpen, setDriveDialogOpen] = useState(false);
    const openFromDrive = useCallback(() => setDriveDialogOpen(true), []);

    const pickDriveFile = useCallback(async (item: DriveItem) => {
        setDriveDialogOpen(false);
        if (!(await resolveUnsaved())) return;
        void openDriveFile(toTracked(item));
    }, [resolveUnsaved, openDriveFile]);

    // Download the current workbook as .xlsx. No Drive upload.
    const handleDownload = useCallback(() => {
        const ed = editorRef.current;
        if (!ed) return;
        setError(null);
        void downloadWorkbook(ed, stripExtension(fileName), "xlsx").catch((err: unknown) => setError(errorMessage(err)));
    }, [fileName]);

    // New asks its own "discard workbook?" question, then starts blank and drops the Drive binding.
    const newWorkbook = useCallback(async () => {
        if (!(await resolveUnsaved())) return;
        void runLoad(async (ed) => {
            await ed.newWorkbook();
            setTracked(null);
            setFileName("Untitled.xlsx");
        });
    }, [resolveUnsaved, runLoad, setTracked]);

    const openLocal = useCallback(() => fileInputRef.current?.click(), []);

    const [saveAsOpen, setSaveAsOpen] = useState(false);

    // Save As confirm: upload to Drive or download locally, using the
    // chosen filename + filetype.
    const handleSaveAsConfirm = useCallback(async (target: "drive" | "computer", name: string, id: string) => {
        const type = saveTypeOf(id);
        setSaveAsOpen(false);
        const ed = editorRef.current;
        if (!ed) return;
        setError(null);
        const cleanName = stripExtension(name || "Untitled");

        if (target === "computer") {
            try {
                await downloadWorkbook(ed, cleanName, type);
            } catch (err) {
                setError(errorMessage(err));
            }
            return;
        }
        await saveAsNewDriveFile(cleanName, type);
    }, [saveAsNewDriveFile]);

    // The File menu inside the editor's toolbar.
    const fileMenu = useMemo<FileMenuItem[]>(() => [
        { id: "new", label: "New", shortcut: "Ctrl+N", onSelect: newWorkbook },
        { id: "open", label: "Open…", shortcut: "Ctrl+O", onSelect: openLocal },
        { id: "open-drive", label: "Open from Drive", onSelect: openFromDrive },
        { id: "save", label: "Save", shortcut: "Ctrl+S", separatorBefore: true, onSelect: () => void saveNowRef.current("manual") },
        { id: "save-as", label: "Save As…", shortcut: "Ctrl+Shift+S", onSelect: () => setSaveAsOpen(true) },
        { id: "download", label: "Download a copy (.xlsx)", onSelect: handleDownload },
        { id: "print", label: "Print…", shortcut: "Ctrl+P", onSelect: () => void editorRef.current?.print() },
        { id: "history", label: "Version history", separatorBefore: true, onSelect: openHistory },
        { id: "close", label: "Close", separatorBefore: true, onSelect: () => void closeFile() }
    ], [newWorkbook, openLocal, openFromDrive, handleDownload, openHistory, closeFile]);

    // Selection summary (status bar), reported by the editor.
    const [aggregate, setAggregate] = useState<SelectionAggregate | null>(null);

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <ShellHeaderPortal>
                <div className="flex min-w-0 flex-1 items-center gap-2">
                    <FileTitle
                        name={fileName}
                        status={status}
                        inDrive={tracked !== null}
                        loading={loading}
                        onRename={renameFile}
                        onRetry={() => void saveNowRef.current("manual")}
                        location={tracked ? (
                            <DriveLocation
                                fileId={tracked.id}
                                parentId={tracked.parentId}
                                shared={Boolean(tracked.role)}
                                tone="onPrimary"
                                className="hidden sm:flex"
                            />
                        ) : null}
                        accessory={
                            <>
                                {tracked ? (
                                    <HistoryButton
                                        tone="onPrimary"
                                        onClick={openHistory}
                                        disabledReason={tracked.role === "viewer" ? VIEWER_HISTORY_MESSAGE : null}
                                    />
                                ) : null}
                                <CommentsButton
                                    file={tracked}
                                    tone="onPrimary"
                                    openCount={commentCount.count}
                                    active={commentsOpen}
                                    onClick={commentsOpen ? () => setCommentsOpen(false) : openComments}
                                />
                            </>
                        }
                    />
                    <div className="ml-auto flex shrink-0 items-center gap-2 pl-1">
                        <FreezeMenu editor={editorRef} onError={setError} />
                        <ShareButton file={tracked} tone="onPrimary" />
                        <CloseFileButton appName="Workbook" busy={closing} onClose={() => void closeFile()} />
                    </div>
                </div>
            </ShellHeaderPortal>
            {historyOpen && tracked ? (
                <VersionHistoryPanel
                    file={tracked}
                    onClose={() => setHistoryOpen(false)}
                    refreshKey={status}
                    saving={status === "saving"}
                    hasUnsavedChanges={hasUnsavedChanges}
                    onRestore={restoreVersion}
                />
            ) : null}
            <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xlsm,.csv,.json"
                hidden
                onChange={onLocalPick}
            />
            {error ? (
                <p role="alert" className="flex shrink-0 items-start gap-2 border-b bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
                    <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    <span>{error}</span>
                </p>
            ) : null}
            {/* Body row: the workbook, and Comments docked beside it on lg+
                (mock `.side.r`; an overlay below that). Version history stays
                an overlay; opening either closes the other. */}
            <div className="relative flex min-h-0 flex-1">
                <div className="relative min-h-0 min-w-0 flex-1 bg-[var(--sheets-canvas)]">
                    <Suspense fallback={
                        <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading editor…
                        </div>
                    }>
                        <WorkbookEditor
                            ref={editorRef}
                            forms={{ fileId: tracked?.id ?? null, token: () => tokenRef.current }}
                            fileName={fileName}
                            fileMenu={fileMenu}
                            onNew={newWorkbook}
                            onOpen={openLocal}
                            onReady={loadInitial}
                            onDirty={markDirty}
                            onSelectionSummary={setAggregate}
                            hideStats
                        />
                    </Suspense>
                </div>
                {commentsOpen && tracked ? (
                    <CommentsPanel
                        key={tracked.id}
                        file={tracked}
                        adapter={commentsAdapter}
                        docked
                        onClose={() => setCommentsOpen(false)}
                        onOpenCountChange={commentCount.set}
                    />
                ) : null}
            </div>
            <SheetStatusBar
                aggregate={aggregate}
                status={status}
                inDrive={tracked !== null}
                loading={loading}
                onRetry={() => void saveNowRef.current("manual")}
            />
            {driveDialogOpen ? (
                <DriveOpenDialog onClose={() => setDriveDialogOpen(false)} onPick={pickDriveFile} />
            ) : null}
            {saveAsOpen ? (
                <SaveAsDialog
                    defaultName={stripExtension(fileName)}
                    defaultType={inferSaveId(fileName)}
                    loadFormats={() => editorRef.current?.saveAsFormats() ?? Promise.resolve([])}
                    loadLimits={(id) => editorRef.current?.saveAsLimits(id) ?? Promise.resolve([])}
                    onClose={() => setSaveAsOpen(false)}
                    onConfirm={handleSaveAsConfirm}
                />
            ) : null}
        </div>
    );
}

/** The live workbook as file bytes: .xlsx written by the engine, or the active sheet as .csv. */
async function renderWorkbook(ed: WorkbookEditorHandle, type: SheetFormat): Promise<Blob> {
    const blob = type === "csv" ? await ed.exportCsv() : await ed.exportXlsx();
    if (blob.size === 0) throw new Error("The workbook came out empty — nothing was saved.");
    return blob;
}

/** The workbook as a file of the wanted type, and the extension that file takes. */
async function renderAs(ed: WorkbookEditorHandle, wanted: SaveType): Promise<{ blob: Blob; ext: string }> {
    if (wanted.startsWith("fmt:")) {
        const id = wanted.slice(4);
        const info = (await ed.saveAsFormats()).find((f) => f.id === id);
        if (!info) throw new Error(`Unknown file type ${id}.`);
        const blob = await ed.exportAs(id);
        if (blob.size === 0) throw new Error("The workbook came out empty — nothing was saved.");
        return { blob, ext: info.ext };
    }
    const type = await resolveFormat(ed, wanted as SheetFormat);
    return { blob: await renderWorkbook(ed, type), ext: type };
}

/** A Save As list id as what the page writes: .xlsx (which becomes .xlsm when the workbook has macros) and CSV UTF-8 keep Drive's own path, the rest go through the engine. */
function saveTypeOf(id: string): SaveType {
    return id === "xlsx" ? id : id === "csv-utf8" ? "csv" : `fmt:${id}`;
}

/** The Save As list entry a file name's extension asks for. */
function inferSaveId(name: string): string {
    const ext = name.match(/\.([^.]+)$/)?.[1]?.toLowerCase();
    switch (ext) {
        case "xlsm": case "xltx": case "xltm": case "xlsb": case "xls": case "ods": case "html":
            return ext;
        case "csv": return "csv-utf8";
        case "txt": return "unicode";
        case "htm": return "html";
        default: return "xlsx";
    }
}

/** Save to the computer: render and download. */
async function downloadWorkbook(ed: WorkbookEditorHandle, baseName: string, wanted: SaveType): Promise<void> {
    const { blob, ext } = await renderAs(ed, wanted);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${baseName}.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** The format a workbook is really written in: one with macros is a macro-enabled .xlsm, one without an .xlsx. */
async function resolveFormat(ed: WorkbookEditorHandle, wanted: SheetFormat): Promise<SheetFormat> {
    if (wanted === "csv") return "csv";
    return (await ed.hasMacros()) ? "xlsm" : "xlsx";
}

/**
 * The format Save must write to version `name` in place, or null when a
 * version would change the file's format (.json snapshots, legacy .xls, anything else).
 * Note .csv versions hold the active sheet's values only — as in Excel.
 */
function versionFormat(name: string): SheetFormat | null {
    const ext = name.match(/\.([^.]+)$/)?.[1]?.toLowerCase();
    return ext === "xlsx" || ext === "xlsm" || ext === "csv" ? ext : null;
}

function toTracked(item: DriveItem): TrackedFile {
    return { id: item.id, name: item.name, parentId: item.parentId, role: item.role ?? null };
}

function unsupportedMessage(name: string): string {
    if (/\.xls$/i.test(name)) return `${name} is an old 97-2003 .xls file, which Workbook can't open yet. Save it as .xlsx first.`;
    return `${name} can't be opened in Workbook. Supported file types are .xlsx, .xlsm, .csv and .json.`;
}

function errorMessage(err: unknown): string {
    return err instanceof Error ? err.message : String(err);
}

// Keeps the file's extension when the user types a bare name ("Budget" →
// "Budget.xlsx"). A different spreadsheet extension is swapped for the
// current one — a rename must never change what format the bytes are in.
function keepExtension(typed: string, current: string): string {
    const trimmed = typed.trim();
    const ext = current.match(/\.([^./\\]+)$/)?.[1];
    if (!ext) return trimmed;
    if (trimmed.toLowerCase().endsWith(`.${ext.toLowerCase()}`) && trimmed.length > ext.length + 1) return trimmed;
    const base = trimmed.replace(KNOWN_EXT_RE, "").trim();
    if (!base) throw new Error("Enter a file name.");
    return `${base}.${ext}`;
}

/** The types offered until the engine has listed its own (it always lists these two). */
const BASIC_SAVE_TYPES: SaveAsFormat[] = [
    { id: "xlsx", ext: "xlsx", label: "Workbook (*.xlsx)", mime: "" },
    { id: "csv-utf8", ext: "csv", label: "CSV UTF-8 (Comma delimited) (*.csv)", mime: "" }
];

function SaveAsDialog({
    defaultName, defaultType, loadFormats, loadLimits, onClose, onConfirm
}: {
    defaultName: string;
    defaultType: string;
    loadFormats: () => Promise<SaveAsFormat[]>;
    loadLimits: (id: string) => Promise<string[]>;
    onClose: () => void;
    onConfirm: (target: "drive" | "computer", name: string, type: string) => void;
}) {
    const [target, setTarget] = useState<"drive" | "computer">("drive");
    const [name, setName] = useState(defaultName);
    const [type, setType] = useState<string>(defaultType);
    const [formats, setFormats] = useState<SaveAsFormat[]>(BASIC_SAVE_TYPES);
    const [limits, setLimits] = useState<string[]>([]);
    // The loaders are new functions each render; the lists are asked for once and when the type changes.
    const loaders = useRef({ loadFormats, loadLimits });
    loaders.current = { loadFormats, loadLimits };
    useEffect(() => {
        let alive = true;
        void loaders.current.loadFormats().then((list) => { if (alive && list.length) setFormats(list); }).catch(() => undefined);
        return () => { alive = false; };
    }, []);
    // What the chosen type leaves out, said before it is saved.
    useEffect(() => {
        let alive = true;
        void loaders.current.loadLimits(type).then((l) => { if (alive) setLimits(l); }).catch(() => { if (alive) setLimits([]); });
        return () => { alive = false; };
    }, [type]);
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
            <div
                role="dialog"
                aria-label="Save As"
                className="w-[460px] max-w-[92vw] overflow-hidden rounded-lg border bg-popover text-popover-foreground shadow-xl flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b px-4 py-3">
                    <h2 className="text-sm font-semibold">Save As</h2>
                    <button type="button" onClick={onClose} className="text-sm text-muted-foreground hover:text-foreground">✕</button>
                </div>
                <form
                    id="sheet-save-as-form"
                    className="flex flex-col gap-3 p-4"
                    onSubmit={(e) => {
                        e.preventDefault();
                        if (name.trim()) onConfirm(target, name.trim(), type);
                    }}
                >
                    <div>
                        <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">Save to</label>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setTarget("drive")}
                                className={`flex-1 rounded-md border px-3 py-2 text-sm ${target === "drive" ? "border-primary bg-primary/10 text-primary" : "hover:bg-accent"}`}
                            >
                                Drive
                            </button>
                            <button
                                type="button"
                                onClick={() => setTarget("computer")}
                                className={`flex-1 rounded-md border px-3 py-2 text-sm ${target === "computer" ? "border-primary bg-primary/10 text-primary" : "hover:bg-accent"}`}
                            >
                                Computer
                            </button>
                        </div>
                    </div>
                    <div>
                        <label htmlFor="save-as-name" className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">Filename</label>
                        <input
                            id="save-as-name"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            autoFocus
                            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        />
                    </div>
                    <div>
                        <label htmlFor="save-as-type" className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">File type</label>
                        <select
                            id="save-as-type"
                            value={type}
                            onChange={(e) => setType(e.target.value)}
                            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        >
                            {formats.map((f) => (
                                <option key={f.id} value={f.id}>{f.label}</option>
                            ))}
                        </select>
                        {limits.length ? (
                            <ul role="status" className="mt-2 list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">
                                {limits.map((l) => <li key={l}>{l}</li>)}
                            </ul>
                        ) : null}
                    </div>
                </form>
                <div className="flex items-center justify-end gap-2 border-t bg-muted/30 px-4 py-3">
                    <button type="button" onClick={onClose} className="rounded-md px-3 py-1.5 text-sm hover:bg-accent">Cancel</button>
                    <button
                        type="submit"
                        form="sheet-save-as-form"
                        disabled={!name.trim()}
                        className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
}

function stripExtension(name: string): string {
    const trimmed = (name ?? "").trim();
    if (!trimmed) return "Untitled";
    return trimmed.replace(/\.[^./\\]+$/, "");
}
